/**
 * As automações arquivadas, num painel à direita, com "Desarquivar".
 *
 * Arquivar tira a automação da lista do dia a dia sem apagar nada; este painel
 * é o caminho de volta, ao lado de "Ver execuções". Desarquivar faz a
 * automação voltar como rascunho, pela mesma regra do menu (`comandosDe`):
 * quem não pode vê o motivo no botão.
 *
 * O comando é o da tela: o recado de sucesso ou de falha chega por props e
 * aparece aqui dentro, onde a pessoa está olhando.
 */

import type { ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import type { AutomacoesDaApi } from '@cliqcrm/contracts';
import { Botao, Dialogo, Icone } from '@/shared/ui';
import { Estado } from '@/shared/ui/estados/estados';
import { Faixa } from '@/shared/ui/estados/faixa';
import { comandosDe, iconeDaAutomacao, type Automacao } from '../lib/automacoes';
import './automacoes-arquivadas.css';

export function AutomacoesArquivadas({
  arquivadas,
  papel,
  nomeDe,
  resumoDe,
  emAndamento,
  recado,
  falha,
  aoDesarquivar,
  aoFechar,
}: {
  arquivadas: UseQueryResult<AutomacoesDaApi>;
  papel: string | null;
  nomeDe: (a: Automacao) => string;
  resumoDe: (a: Automacao) => string;
  /** A automação cujo comando está em curso. */
  emAndamento: string | undefined;
  /** O recado do último comando: "… voltou como rascunho." */
  recado: string | null;
  falha: string | null;
  aoDesarquivar: (a: Automacao) => void;
  aoFechar: () => void;
}): ReactNode {
  return (
    <Dialogo titulo="Automações arquivadas" forma="lateral" aoFechar={aoFechar}>
      <div className="automacoes-arquivadas">
        <p className="automacoes-arquivadas__apoio">
          Arquivadas não rodam nem aparecem na lista. Desarquivada, a automação volta como rascunho:
          confira e ligue de novo.
        </p>
        {recado && (
          <p className="automacoes-arquivadas__recado" role="status">
            <Icone nome="check" />
            {recado}
          </p>
        )}
        {falha && <Faixa tom="bloqueado">{falha}</Faixa>}
        <Estado
          consulta={arquivadas}
          forma="lista"
          linhas={3}
          vazio={{
            quando: (d) => d.itens.length === 0,
            titulo: 'Nenhuma automação arquivada',
            porque: 'O que for arquivado pelo menu da automação aparece aqui.',
          }}
        >
          {(dados) => (
            <ul className="automacoes-arquivadas__lista">
              {dados.itens.map((a) => {
                const nome = nomeDe(a);
                const pode = comandosDe(a, papel).includes('desarquivar');
                const mudando = emAndamento === a.id;
                return (
                  <li key={a.id} className="automacoes-arquivadas__item">
                    <span className="automacoes-arquivadas__tipo" aria-hidden="true">
                      <Icone nome={iconeDaAutomacao(a)} />
                    </span>
                    <div className="automacoes-arquivadas__texto">
                      <strong className="automacoes-arquivadas__nome">{nome}</strong>
                      <span className="automacoes-arquivadas__resumo">{resumoDe(a)}</span>
                    </div>
                    <Botao
                      variante="secundario"
                      miudo
                      aria-label={`Desarquivar ${nome}`}
                      desabilitado={!pode || emAndamento !== undefined}
                      motivo={
                        !pode
                          ? 'O seu papel não desarquiva esta automação.'
                          : 'Espere o comando anterior terminar.'
                      }
                      aria-busy={mudando || undefined}
                      onClick={() => aoDesarquivar(a)}
                    >
                      <Icone nome="repetir" />
                      {mudando ? 'Desarquivando…' : 'Desarquivar'}
                    </Botao>
                  </li>
                );
              })}
            </ul>
          )}
        </Estado>
      </div>
    </Dialogo>
  );
}
